import * as React from 'react';
import moment, { type Moment } from 'moment-timezone';
import { ConfigProvider, Flex, Space, DatePicker, Button, theme } from 'antd';
import {
  getRangeValue,
  mergeDateTimeWithPrevDate,
  getDisabledTimeHours,
  getRangeArray,
  checkDateValueValid,
  formatDuration,
  getAvailableDateRange,
} from './utils';
import { PopupContainer, ExtraContainer, Bold } from './styled';

const { RangePicker, TimePicker } = DatePicker as ReturnType<
  typeof DatePicker.generatePicker<Moment>
>;

export interface DateTimePickerProps
  extends Omit<
    GetProps<typeof RangePicker>,
    | 'value'
    | 'defaultValue'
    | 'open'
    | 'showTime'
    | 'allowClear'
    | 'inputReadOnly'
    | 'format'
    | 'minDate'
    | 'maxDate'
    | 'disabledDate'
    | 'disabledTime'
    | 'renderExtraFooter'
    | 'panelRender'
    | 'mode'
    | 'needConfirm'
    | 'picker'
    | 'onOpenChange'
    | 'onChange'
  > {
  defaultValue?: [string, string];
  value?: [string, string];
  /**
   * Selectable range limits
   */
  range?: [string | undefined, string | undefined];
  /**
   * Maximum selectable range in milliseconds
   */
  maxLength?: number;
  onChange?: (value: [string, string]) => void;
}

const DISABLED_START_DATE_MINUTES = getRangeArray(1, 59);
const DISABLED_END_DATE_MINUTES = getRangeArray(0, 58);
const ERROR_TEXTS = {
  NIL: 'Please select start and end date time.',
  START_AFTER_END: 'Start date time cannot be after end date time.',
  EXCEED_MAX_LENGTH: (maxLength: number) =>
    `Selected date range exceeds maximum allowed range (${formatDuration(maxLength)}).`,
};

const DateTimePicker = (props: DateTimePickerProps) => {
  const { defaultValue, value, range, maxLength = Infinity, onChange, ...p } = props;
  const { token } = theme.useToken();
  const ref = React.useRef<GetRef<typeof RangePicker>>(null);
  const popupRef = React.useRef<HTMLDivElement>(null);
  const [open, setOpen] = React.useState(false);
  const [innerValue, setInnerValue] = React.useState(() => getRangeValue(value ?? defaultValue));
  const [dateRange, setDateRange] = React.useState(() => getAvailableDateRange(range));
  const latestValueRef = React.useRef<[Moment, Moment] | undefined>(innerValue);

  const disabledDate = React.useCallback<GetProp<typeof RangePicker, 'disabledDate'>>(
    (current, info) => {
      if (info.type === 'date' && info.from) {
        return Math.abs(current.diff(info.from, 'milliseconds')) > maxLength;
      }

      return false;
    },
    [maxLength],
  );

  const renderExtraFooter = React.useCallback<GetProp<typeof RangePicker, 'renderExtraFooter'>>(
    (mode) => {
      if (mode === 'date') {
        const valueCheckResult = checkDateValueValid(innerValue, maxLength);
        const valueInvalid = valueCheckResult !== 'VALID';
        const timerStatus =
          valueCheckResult === 'VALID' || valueCheckResult === 'NIL' ? undefined : 'error';
        const maxEndDateTimeCandidates = [
          dateRange?.[1],
          // Calculate the maximum end date based on the selected start date and maxLength.
          // Have to subtract 1 millisecond due to the closed interval of date range
          innerValue?.[0] ? innerValue[0].clone().add(maxLength - 1, 'milliseconds') : undefined,
        ].filter(Boolean) as Moment[];

        return (
          <ConfigProvider getPopupContainer={() => popupRef.current ?? document.body}>
            <ExtraContainer>
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Flex align="end" justify="space-between">
                  <Space>
                    <Bold>{innerValue?.[0].format('MMM DD YYYY') ?? 'Start Date'}</Bold>
                    <TimePicker
                      value={innerValue?.[0]}
                      inputReadOnly
                      hideDisabledOptions
                      allowClear={false}
                      showNow={false}
                      needConfirm={false}
                      status={timerStatus}
                      disabled={!innerValue?.[0]}
                      disabledTime={(date) => ({
                        disabledMinutes: () => DISABLED_START_DATE_MINUTES,
                        disabledHours: getDisabledTimeHours(date, dateRange),
                      })}
                      format="HH:mm"
                      placeholder="00:00"
                      classNames={{ popup: { root: 'minute-disabled' } }}
                      styles={{ root: { width: 86 } }}
                      onChange={(date) => {
                        setInnerValue((v) => {
                          if (!Array.isArray(v)) return v;
                          return [mergeDateTimeWithPrevDate(date, v[0]), v[1]];
                        });
                      }}
                      onCalendarChange={(date) => {
                        setInnerValue((v) => {
                          if (!Array.isArray(v)) return v;
                          return [mergeDateTimeWithPrevDate(date as Moment, v[0]), v[1]];
                        });
                      }}
                    />
                    <span>to</span>
                    <Bold>{innerValue?.[1].format('MMM DD YYYY') ?? 'End Date'}</Bold>
                    <TimePicker
                      value={innerValue?.[1]}
                      inputReadOnly
                      hideDisabledOptions
                      allowClear={false}
                      showNow={false}
                      needConfirm={false}
                      status={timerStatus}
                      disabled={!innerValue?.[1]}
                      disabledTime={(date) => ({
                        disabledMinutes: () => DISABLED_END_DATE_MINUTES,
                        disabledHours: getDisabledTimeHours(date, [
                          innerValue?.[0] ?? dateRange?.[0],
                          maxEndDateTimeCandidates.length > 0
                            ? moment.min(maxEndDateTimeCandidates)
                            : undefined,
                        ]),
                      })}
                      format="HH:mm"
                      placeholder="23:59"
                      classNames={{ popup: { root: 'minute-disabled' } }}
                      styles={{ root: { width: 86 } }}
                      onChange={(date) => {
                        setInnerValue((v) => {
                          if (!Array.isArray(v)) return v;
                          return [v[0], mergeDateTimeWithPrevDate(date, v[1])];
                        });
                      }}
                      onCalendarChange={(date) => {
                        setInnerValue((v) => {
                          if (!Array.isArray(v)) return v;
                          return [v[0], mergeDateTimeWithPrevDate(date as Moment, v[1])];
                        });
                      }}
                    />
                  </Space>
                  <Button
                    type="primary"
                    disabled={valueInvalid}
                    onClick={() => {
                      if (innerValue && innerValue[0] && innerValue[1]) {
                        latestValueRef.current = innerValue;
                        onChange?.([innerValue[0].toISOString(), innerValue[1].toISOString()]);
                        setOpen(false);
                      }
                    }}
                  >
                    Apply
                  </Button>
                </Flex>
                {ERROR_TEXTS[valueCheckResult] && (
                  <span style={{ color: token.colorError }}>
                    {typeof ERROR_TEXTS[valueCheckResult] === 'function'
                      ? ERROR_TEXTS[valueCheckResult](maxLength)
                      : ERROR_TEXTS[valueCheckResult]}
                  </span>
                )}
              </Space>
            </ExtraContainer>
          </ConfigProvider>
        );
      }

      return null;
    },
    [onChange, token.colorError, maxLength, dateRange, innerValue],
  );

  const panelRender = React.useCallback<GetProp<typeof RangePicker, 'panelRender'>>(
    (originPanel) => {
      return <PopupContainer ref={popupRef}>{originPanel}</PopupContainer>;
    },
    [],
  );

  const handleOpenChange = React.useCallback<GetProp<typeof RangePicker, 'onOpenChange'>>((v) => {
    if (v) {
      setOpen(v);
    }
  }, []);

  const handleRangeChange = React.useCallback<GetProp<typeof RangePicker, 'onChange'>>(
    (dates) => {
      // Doesn't allow single date selection
      if (Array.isArray(dates) && dates[0] && dates[1]) {
        const start = dates[0].clone().startOf('day');
        const end = dates[1].clone().endOf('day');

        setInnerValue([
          dateRange?.[0] && start.isBefore(dateRange[0]) ? dateRange[0] : start,
          dateRange?.[1] && end.isAfter(dateRange[1]) ? dateRange[1] : end,
        ]);
      }
    },
    [dateRange],
  );

  React.useEffect(() => {
    setInnerValue(value ? getRangeValue(value) : latestValueRef.current);
  }, [value, open]);

  React.useEffect(() => {
    setDateRange(getAvailableDateRange(range));
  }, [range]);

  // When range's end is undefined, update dateRange's end to current time on picker open
  React.useEffect(() => {
    if (open && !range?.[1]) {
      setDateRange(getAvailableDateRange(range));
    }
  }, [open, range]);

  React.useEffect(() => {
    const handler = (e: PointerEvent) => {
      if (
        popupRef.current &&
        ref.current?.nativeElement &&
        !popupRef.current.contains(e.target as Node) &&
        !ref.current.nativeElement.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    window.addEventListener('click', handler, { capture: true });

    return () => {
      window.removeEventListener('click', handler, { capture: true });
    };
  }, []);

  return (
    <RangePicker
      {...p}
      ref={ref}
      open={open}
      value={innerValue}
      allowClear={false}
      inputReadOnly
      format="YYYY/MM/DD HH:mm"
      minDate={dateRange?.[0]}
      maxDate={dateRange?.[1]}
      disabledDate={disabledDate}
      renderExtraFooter={renderExtraFooter}
      panelRender={panelRender}
      styles={{ ...p.styles, root: { width: 310, ...p.styles?.root } }}
      onOpenChange={handleOpenChange}
      onChange={handleRangeChange}
    />
  );
};

export default React.memo(DateTimePicker);
